import React, { useMemo, useState, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  Node,
  Edge,
  NodeMouseHandler,
  OnInit,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { OrgChartNode } from '../../types';
import OrgChartPersonNode, { OrgChartPersonNodeData } from './OrgChartPersonNode';
import OrgChartJunctionNode, { OrgChartJunctionNodeData } from './OrgChartJunctionNode';
import { transformOrgChartToGraph } from './transform';

type AnyNodeData = OrgChartPersonNodeData | OrgChartJunctionNodeData;

const nodeTypes = {
  person: OrgChartPersonNode,
  junction: OrgChartJunctionNode,
};

interface OrgChartFlowProps {
  data: OrgChartNode[];
}

const OrgChartFlow: React.FC<OrgChartFlowProps> = ({ data }) => {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  
  const { nodes: baseNodes, edges: baseEdges } = useMemo(
    () => transformOrgChartToGraph(data),
    [data]
  );

  const childrenMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    baseEdges.forEach((edge) => {
      if (!map.has(edge.source)) {
        map.set(edge.source, new Set());
      }
      map.get(edge.source)!.add(edge.target);
    });
    return map;
  }, [baseEdges]);

  const getDescendants = useCallback(
    (nodeId: string): Set<string> => {
      const descendants = new Set<string>();
      const queue = [nodeId];
      while (queue.length > 0) {
        const current = queue.shift()!;
        const children = childrenMap.get(current);
        if (children) {
          children.forEach((child) => {
            descendants.add(child);
            queue.push(child);
          });
        }
      }
      return descendants;
    },
    [childrenMap]
  );

  const highlightedNodes = useMemo(() => {
    if (!hoveredNodeId) return new Set<string>();
    const descendants = getDescendants(hoveredNodeId);
    descendants.add(hoveredNodeId);
    return descendants;
  }, [hoveredNodeId, getDescendants]);

  const nodes: Node<AnyNodeData>[] = useMemo(() => {
    return baseNodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        isHighlighted: highlightedNodes.size === 0 || highlightedNodes.has(node.id),
        isDimmed: highlightedNodes.size > 0 && !highlightedNodes.has(node.id),
      },
    }));
  }, [baseNodes, highlightedNodes]);

  const handleNodeMouseEnter: NodeMouseHandler = useCallback((_, node) => {
    setHoveredNodeId((prevId) => (prevId === node.id ? prevId : node.id));
  }, []);

  const handleNodeMouseLeave: NodeMouseHandler = useCallback(() => {
    setHoveredNodeId((prevId) => (prevId === null ? prevId : null));
  }, []);

  const handleInit: OnInit<Node<AnyNodeData>, Edge> = useCallback((instance) => {
    instance.fitView({
      padding: 0.2,
      minZoom: 0.1,
      maxZoom: 1.5,
    });
  }, []);

  const edges: Edge[] = useMemo(() => {
    return baseEdges.map((edge) => {
      const isHighlighted =
        highlightedNodes.size === 0 ||
        (highlightedNodes.has(edge.source) && highlightedNodes.has(edge.target));
      const isDimmed = highlightedNodes.size > 0 && !isHighlighted;

      return {
        ...edge,
        style: {
          stroke: isHighlighted ? '#3b82f6' : '#94a3b8',
          strokeWidth: isHighlighted ? 3 : 2,
          opacity: isDimmed ? 0.2 : 1,
          transition: 'all 0.2s ease-in-out',
        },
        animated: isHighlighted && highlightedNodes.size > 0,
      };
    });
  }, [baseEdges, highlightedNodes]);

  return (
    <div className="w-full h-[calc(100vh-200px)] min-h-[500px] bg-gray-50 rounded-lg border">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        onInit={handleInit}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnScroll
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e2e8f0" />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(node) => node.type === 'junction' ? 'transparent' : '#3b82f6'}
          maskColor="rgba(0, 0, 0, 0.1)"
          className="bg-white border rounded"
        />
      </ReactFlow>
    </div>
  );
};

export default OrgChartFlow;
